import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Home from '../pages/Home'
import Login from '../pages/Auth/Login'
import Register from '../pages/Auth/Register'
import ForgotPassword from '../pages/Auth/ForgotPassword'
import CourseList from '../pages/Courses/CourseList'
import CourseDetail from '../pages/Courses/CourseDetail'
import MyCourses from '../pages/Courses/MyCourses'
import LecturePlayer from '../pages/Learning/LecturePlayer'
import Notes from '../pages/Learning/Notes'
import Exams from '../pages/Learning/Exams'
import ExamTaking from '../pages/Learning/ExamTaking'
import ExamResult from '../pages/Learning/ExamResult'
import Questions from '../pages/Learning/Questions'
import Profile from '../pages/Profile/Profile'
import MyProgress from '../pages/Profile/MyProgress'
import Subscriptions from '../pages/Subscriptions/Subscriptions'
import Payments from '../pages/Payments/Payments'
import PackageSubjectSelection from '../pages/Payments/PackageSubjectSelection'
import Notifications from '../pages/Notifications/Notifications'
import Search from '../pages/Search/Search'
import Community from '../pages/Community/Community'
import CommunityPost from '../pages/Community/CommunityPost'
import CreatePost from '../pages/Community/CreatePost'
import Reels from '../pages/Reels/Reels'
import Chat from '../pages/Chat/Chat'
import Favorites from '../pages/Favorites/Favorites'
import Instructors from '../pages/Instructors/Instructors'
import InstructorProfile from '../pages/Instructors/InstructorProfile'
import Settings from '../pages/Settings/Settings'

// Private route - requires authentication
function PrivateRoute({ children }) {
  try {
    const { user, loading } = useAuth()
    
    if (loading) {
      return <div>Loading...</div>
    }
    
    return user ? children : <Navigate to="/login" replace />
  } catch (error) {
    // If context is not available, redirect to login
    return <Navigate to="/login" replace />
  }
}

// Public route - shows content but may prompt login for actions
function PublicRoute({ children }) {
  return children
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/courses" element={<CourseList />} />
      <Route path="/courses/:id" element={<CourseDetail />} />
      <Route path="/search" element={<Search />} />
      <Route path="/instructors" element={<Instructors />} />
      <Route path="/instructors/:id" element={<InstructorProfile />} />
      <Route path="/community" element={<Community />} />
      <Route 
        path="/community/create" 
        element={
          <PrivateRoute>
            <CreatePost />
          </PrivateRoute>
        } 
      />
      <Route path="/community/posts/:id" element={<CommunityPost />} />
      <Route path="/reels" element={<Reels />} />
      <Route path="/subscriptions" element={<Subscriptions />} />
      <Route 
        path="/packages/:packageId/subjects" 
        element={
          <PrivateRoute>
            <PackageSubjectSelection />
          </PrivateRoute>
        } 
      />
      
      {/* Protected Routes - Require Login */}
      <Route 
        path="/courses/:courseId/lectures/:lectureId" 
        element={
          <PrivateRoute>
            <LecturePlayer />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/my-courses" 
        element={
          <PrivateRoute>
            <MyCourses />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/notes" 
        element={
          <PrivateRoute>
            <Notes />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/exams" 
        element={
          <PrivateRoute>
            <Exams />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/exams/:id/take" 
        element={
          <PrivateRoute>
            <ExamTaking />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/exams/:id/result" 
        element={
          <PrivateRoute>
            <ExamResult />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/courses/:id/questions" 
        element={
          <PrivateRoute>
            <Questions />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/favorites" 
        element={
          <PrivateRoute>
            <Favorites />
          </PrivateRoute>
        } 
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile/progress"
        element={
          <PrivateRoute>
            <MyProgress />
          </PrivateRoute>
        }
      />
      <Route 
        path="/payments" 
        element={
          <PrivateRoute>
            <Payments />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <PrivateRoute>
            <Notifications />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <PrivateRoute>
            <Chat />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default AppRoutes
